// ============================================================================
// MODEL-SPECIFIC CONFIG (Phase A - log-only)
// ============================================================================
// Centralises per-model + per-task request-body configuration so we can:
//   - sanitise args the current model's family can't accept
//     (e.g. stripping response_format for models that don't support it)
//   - lift per-task token/temperature/response_format defaults out of each caller
//   - make user overrides the highest-priority layer
//
// PHASE A CONTRACT: this module is CALLED in log-only mode from ai-providers.js
// buildBody(). The body returned to fetch is still the legacy body. The
// proposed body produced here is ONLY diff-logged when the user opts in via
// `window.QM_LOG_MODEL_CONFIG_DIFF = true`. Zero behaviour change.
//
// PHASE B (future) flips the return to use `proposed` for low-stakes tasks.
// PHASE C (future) widens it to all tasks. PHASE D (future) removes the
// legacy builder.
//
// Kill switches (reserved - not wired in Phase A):
//   localStorage.qm_enable_model_config_phase_b = 'true'
//   localStorage.qm_disable_model_config         = 'true'
//
// 4-layer merge (lowest → highest precedence):
//   1. FAMILY_DEFAULTS[family]
//   2. TASK_DEFAULTS[task]
//   3. FAMILY_TASK_OVERRIDES[family][task]
//   4. userOverrides argument (temperature / maxTokens / responseFormat)
// Then: family-sanitisation step strips fields the family can't accept.
// ============================================================================

// ── Family detection ────────────────────────────────────────────────────────
// Maps OpenRouter model IDs to their underlying provider family. OpenRouter's
// namespaced format is `provider/model`, so the prefix is the signal.
export function detectFamily(modelId) {
    if (!modelId || typeof modelId !== 'string') return 'unknown';
    const id = modelId.toLowerCase();
    if (id.startsWith('anthropic/')) return 'anthropic';
    if (id.startsWith('google/')) return 'google';
    if (id.startsWith('openai/')) return 'openai';
    if (id.startsWith('xai/') || id.startsWith('x-ai/')) return 'xai';
    if (id.startsWith('moonshotai/') || id.startsWith('moonshot/')) return 'moonshot';
    if (id.startsWith('qwen/') || id.startsWith('alibaba/')) return 'qwen';
    if (id.startsWith('deepseek/')) return 'deepseek';
    if (id.startsWith('mistralai/') || id.startsWith('mistral/')) return 'mistral';
    if (id.startsWith('meta-llama/') || id.startsWith('meta/')) return 'meta';
    if (id.startsWith('cohere/')) return 'cohere';
    return 'unknown';
}

export function isGemini3Model(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    const id = modelId.toLowerCase();
    return id.startsWith('google/gemini-3');
}

export function isKimiK26Model(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    const id = modelId.toLowerCase();
    return id.startsWith('moonshotai/kimi-k2.6')
        || id.startsWith('moonshot/kimi-k2.6')
        || id.includes('kimi-k2-6');
}

// Kimi models with a fixed sampling contract. K3.1 Pro's catalogue entry
// (2026-10-26) advertises neither temperature nor top_p.
export function isKimiFixedSamplingModel(modelId) {
    if (isKimiK26Model(modelId)) return true;
    return typeof modelId === 'string' && modelId.toLowerCase().startsWith('moonshotai/kimi-k3.1-pro');
}

// ── Reasoning-model detection ───────────────────────────────────────────────
// Reasoning models (o-series, Opus 4.7) have different quirks:
//   - they emit a private chain-of-thought before the final answer
//   - temperature is ignored or restricted (OpenAI o-series)
//   - they need generous output budgets so the CoT doesn't eat the response
// Phase A doesn't act on this, but Phase B will use it to upgrade maxTokens.
export function isReasoningModel(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    const id = modelId.toLowerCase();
    // OpenAI o-series: o1, o1-mini, o3, o3-mini, o4-mini, etc.
    if (/^openai\/o\d/.test(id)) return true;
    // Anthropic Opus 4.x (4.5 / 4.6 / 4.7 / 4.8 …) - reasoning-mode GA. Broadened
    // from the 4.7-only check (2026-06-24) so model-version bumps (e.g. 4.7→4.8)
    // keep the reasoning_effort budget instead of silently dropping it.
    if (id.includes('opus-4')) return true;
    // Anthropic Sonnet 5: the first reasoning-era Sonnet. Live OpenRouter probe
    // (2026-07-11): every claude-sonnet-5 endpoint advertises `reasoning` +
    // `reasoning_effort` (and notably does NOT support `temperature` — the
    // gateway capability filter strips it, so determinism comes from prompts).
    // Before this clause the QM Assistant's normal route (Sonnet 5 default)
    // silently dropped its TASK_DEFAULTS reasoning_effort. Anchored to
    // sonnet-5 so the non-reasoning sonnet-4.x line stays excluded; ':' is a
    // valid boundary too so OpenRouter variant suffixes (:beta/:nitro/:floor)
    // keep their reasoning effort (review fix 2026-07-11).
    if (/^anthropic\/claude-sonnet-5(?:[.:-]|$)/.test(id)) return true;
    // OpenAI GPT-5 family: every non-chat sibling exposes OpenRouter's unified
    // `reasoning` param (which carries effort). The `-chat` variants
    // (gpt-5-chat / -chat-latest / 5.2-chat / 5.3-chat) are non-reasoning and
    // would 400 on reasoning.effort, so exclude them. The `(?:[.-]|$)` boundary
    // stops a hypothetical `gpt-50` from matching.
    // Verified: openrouter.ai/api/v1/models/openai/gpt-5-chat/endpoints lists no `reasoning`.
    if (/^openai\/gpt-5(?:[.-]|$)/.test(id) && !id.includes('-chat')) return true;
    // Gemini 3.7 and 3.8 Flash advertise OpenRouter's unified reasoning and
    // exact reasoning_effort parameters. 3.8 has been QuoteMaker's ordinary
    // pipeline default since d45b934d (3.7 stays a manual pick), so task-level
    // effort must reach the gateway instead of being silently discarded as it
    // was for older Gemini Flash routes. The backend gateway still strips
    // `reasoning` for any model whose live catalogue entry lacks it.
    if (/^google\/gemini-3\.[78]-flash(?:[.:-]|$)/.test(id)) return true;
    // Z.ai GLM-5 series (glm-5.2 text + glm-5v-turbo vision) exposes `reasoning`
    // on every OpenRouter endpoint. Anchored to glm-5 so older non-thinking
    // glm-4.x siblings are not swept in. Live probe (2026-06-26) confirmed
    // glm-5.2 reasons by default and accepts both 'high' and 'medium' efforts.
    if (/^z-ai\/glm-5(?:[.v-]|$)/.test(id)) return true;
    // Grok 4.5+ exposes OpenRouter's reasoning/reasoning_effort contract. The
    // new diagnosis and independent-critic lanes depend on high/xhigh reaching
    // the backend gateway rather than silently running at provider default.
    if (/^x-ai\/grok-4\.(?:5|6)(?:[.:-]|$)/.test(id)) return true;
    // Kimi K2.6 exposes reasoning / reasoning_effort through OpenRouter.
    if (isKimiK26Model(id)) return true;
    // 2026-09-29 catalogue: every model registered that day lists `reasoning`
    // and `reasoning_effort`. The version-anchored clauses above miss all of
    // them ('opus-4', gpt-5, grok-4.5/4.6), so their task effort was silently
    // dropped. GPT-6 does not accept temperature, and this clause also makes
    // the OpenAI temperature strip apply to it.
    if (/^anthropic\/claude-opus-5(?:[.:-]|$)/.test(id)) return true;
    if (/^anthropic\/claude-fable-5(?:[.:-]|$)/.test(id)) return true;
    // 2026-10-08 catalogue: Claude Haiku 5.5 lists `reasoning` and
    // `reasoning_effort`, the first Haiku that does, so the haiku-4.x line
    // stays excluded. Sonnet 5.5 matches the Sonnet 5 clause above and
    // GPT-6.1 the GPT-6 clause below.
    if (/^anthropic\/claude-haiku-5(?:[.:-]|$)/.test(id)) return true;
    if (/^openai\/gpt-6(?:[.-]|$)/.test(id) && !id.includes('-chat')) return true;
    if (/^x-ai\/grok-4\.7(?:[.:-]|$)/.test(id)) return true;
    if (/^qwen\/qwen3\.8-max(?:[.:-]|$)/.test(id)) return true;
    if (/^deepseek\/deepseek-v4\.1-flash(?:[.:-]|$)/.test(id)) return true;
    return false;
}

// Measured routes that run at the model's OWN default effort. The 2026-09-29
// Assistant measurement sent these models no reasoning directive (they were
// not registered as reasoning models yet), so the provider default IS the
// measured configuration: DeepSeek V4.1 Flash defaults to 'high', Qwen3.8 Max
// to 'xhigh', Grok 4.7 to 'high'. Sending the Assistant task's 'max' would be
// an unmeasured request: an interrupted 38-turn DeepSeek run at 'max' had an
// 18.7 s median turn (up to 217 s) against 2.5 s at its default, and 5 of its
// turns ended in a failed call.
// Keyed by task, so every other lane keeps its task effort on these models.
export const MEASURED_DEFAULT_EFFORT_ROUTES = Object.freeze({
    'ai-assistant': Object.freeze(['deepseek/deepseek-v4.1-flash', 'qwen/qwen3.8-max-0902', 'x-ai/grok-4.7']),
    'ai-assistant-deep': Object.freeze(['deepseek/deepseek-v4.1-flash', 'qwen/qwen3.8-max-0902', 'x-ai/grok-4.7']),
});

function _runsAtMeasuredDefaultEffort(task, model) {
    const models = MEASURED_DEFAULT_EFFORT_ROUTES[task];
    return Array.isArray(models) && models.includes(String(model || '').trim().toLowerCase());
}

// Measured routes that run at a MEASURED effort other than the provider
// default. Checked before MEASURED_DEFAULT_EFFORT_ROUTES.
// 2026-10-14 (docs/assistant-speed/results-2026-10-14.md, 216 turns on the
// app's own request path, 12 Stage 4 and 5 cases): DeepSeek V4.1 Flash on the
// normal Assistant turn passed 68 of 72 at 'low' against 62 of 72 at its
// default, at or above the default on every case, with a 7.6 s mean turn
// against 10.8 s and a 13.3 s p90 against 21.0 s. Reasoning off was faster but
// worse on the judgement case, so it is not used. Think Harder
// ('ai-assistant-deep') was not measured and keeps its route.
export const MEASURED_EFFORT_OVERRIDES = Object.freeze({
    'ai-assistant': Object.freeze({ 'deepseek/deepseek-v4.1-flash': 'low' }),
});

function _measuredEffortOverride(task, model) {
    const byModel = MEASURED_EFFORT_OVERRIDES[task];
    const effort = byModel && byModel[String(model || '').trim().toLowerCase()];
    return typeof effort === 'string' ? effort : null;
}

// Read the reasoning-effort kill switch (localStorage qm_disable_reasoning_effort).
// Browser-only; a safe no-op on the server / in tests.
function _reasoningEffortDisabled() {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('qm_disable_reasoning_effort') === 'true';
        }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
}

// Resolve the reasoning-effort tier to forward for a (task, model) pair, or null.
// SINGLE SOURCE OF TRUTH shared by the browser-direct path (ai-providers
// buildLegacyBody) and the prod backend-gateway path (data-service-api
// aiGateway). Returns null unless: the kill switch is off, the model is a
// reasoning model, and the task carries a reasoning_effort in TASK_DEFAULTS.
// The live OpenRouter probe (2026-06-26) confirmed this `effort` shape activates
// adaptive thinking for opus-4.x / gpt-5 (given max_tokens headroom, which QM
// has) and tunes GLM-5 (which reasons by default); GLM accepts 'medium' + 'high'
// so no effort clamp is needed.
export function resolveReasoningEffort(task, model) {
    if (!task || _reasoningEffortDisabled() || !isReasoningModel(model)) return null;
    const measured = _measuredEffortOverride(task, model);
    if (measured) return measured;
    if (_runsAtMeasuredDefaultEffort(task, model)) return null;
    const td = TASK_DEFAULTS[task];
    const effort = td && td.reasoning_effort;
    return (effort && typeof effort === 'string') ? effort : null;
}

// ── Default-reasoning suppression (Slice 1, v5.46.44) ────────────────────────
// Some models reason BY DEFAULT (provider-side), independent of whether we send a
// `reasoning` field. On strict-JSON extraction tasks that default thinking
// consumes the max_tokens completion budget and the model returns an EMPTY body
// (the live z-ai/glm-5.2 Work Method failure: only reasoning tokens stream, then
// content is ''). For those (model, task) pairs we proactively send
// reasoning:{enabled:false} so the whole budget goes to the JSON answer. This is
// the PROACTIVE complement to v5.46.43's reactive "escalate to a non-reasoning
// model on empty content" safety net.
//
// Scoped to GLM-5 only for now: it is the proven victim, and OpenRouter's
// reasoning doc + a live probe confirm it is a hybrid that honours enabled:false.
// Gemini-3 also has thinking but our isReasoningModel() excludes it and we have
// NOT verified it accepts reasoning:{enabled:false} (some models flag
// reasoning.mandatory and reject it) — add it here after that check + a smoke.
export function reasonsByDefault(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    // Anchored to glm-5 so non-thinking glm-4.x siblings are excluded (mirrors
    // the GLM clause in isReasoningModel).
    return /^z-ai\/glm-5(?:[.v-]|$)/.test(modelId.toLowerCase());
}

// Strict-JSON extraction / structuring / classification tasks where the model's
// reasoning is NOT a quality lever and an empty body is fatal. Deliberately
// EXCLUDES the deep tasks (diagnosis-*) and the high-budget heavy tasks
// (risk-analysis / quote-generation / project-strategy — 16-32k budgets, normally
// Opus) where reasoning may help and budget headroom makes empty content unlikely.
// Vision tasks are omitted too — GLM-5.2 is text-only, so they never route here.
export const SUPPRESS_DEFAULT_REASONING_TASKS = new Set([
    'cost-recompute',
    'report-synthesis',
    'rfq-mode-classify', 'doc-classify', 'batch-classify',
    'contact-extraction', 'classification-verifier', 'assessor-extraction',
    'notes-scoring', 'notes-score', 'project-summary',
    'evidence-canonicalisation', 'rationale-precompute',
    'gold-standard-extraction', 'pdf-comparison', 'quote-import-extraction',
    'quote-paste-structure',
    // 2026-07-29: the vision-extraction gap. This list was built on the
    // premise that reason-by-default models never route to vision tasks —
    // stale the moment the No-Google gear sent one there. A reason-by-default
    // model on a schema-forced extraction task can burn its budget thinking
    // and return an EMPTY body, which is the 2026-07-28 photo-forensic /
    // defect-locator failure signature. These are read-off-the-image
    // extraction tasks where reasoning buys nothing; diagnosis-pass-a is
    // deliberately NOT here because diagnosis quality wants the thinking.
    'photo-forensic', 'photo-auto-assign',
    'photo-label', 'photo-description', 'image-ocr', 'pdf-ocr',
    'plan-analysis',
    // 2026-10-13: a strict-JSON sort of thumbnails; reasoning buys nothing.
    'image-text-triage',
]);

// Read the suppression kill switch (localStorage qm_disable_reasoning_suppression).
// Independent of qm_disable_reasoning_effort: that one stops effort tiers, this
// one stops the enabled:false suppression.
function _reasoningSuppressionDisabled() {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('qm_disable_reasoning_suppression') === 'true';
        }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
}

// Resolve the reasoning directive string to forward for a (task, model) pair, or
// null. Supersedes resolveReasoningEffort at the gateway: returns an effort tier
// ('low'..'xhigh') when the task wants reasoning on a reasoning model, OR the
// 'none' sentinel (backend maps to reasoning:{enabled:false}) to suppress a
// reason-by-default model's thinking on a strict-JSON task, OR null (leave the
// body unchanged). Effort always wins over suppression: a task that explicitly
// asked for reasoning keeps it.
export function resolveReasoningDirective(task, model) {
    const effort = resolveReasoningEffort(task, model);
    if (effort) return effort;
    if (!task || _reasoningSuppressionDisabled()) return null;
    if (reasonsByDefault(model) && SUPPRESS_DEFAULT_REASONING_TASKS.has(task)) return 'none';
    return null;
}

// ── Family defaults ─────────────────────────────────────────────────────────
// Conservative baselines per family. These act as the floor; task + user
// layers narrow them.
export const FAMILY_DEFAULTS = {
    anthropic: {
        temperature: 0.2,
        maxTokens: 16384,
        // Anthropic supports response_format on OpenRouter but handles
        // {type:'json_object'} as a best-effort hint. OK to pass through.
        responseFormat: null,
    },
    google: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
    openai: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
    xai: {
        temperature: 0.2,
        maxTokens: 8192,
        responseFormat: null,
    },
    moonshot: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
    qwen: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
    deepseek: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
    mistral: {
        temperature: 0.2,
        maxTokens: 8192,
        responseFormat: null,
    },
    meta: {
        temperature: 0.2,
        maxTokens: 8192,
        responseFormat: null,
    },
    cohere: {
        temperature: 0.2,
        maxTokens: 8192,
        responseFormat: null,
    },
    unknown: {
        temperature: 0.2,
        maxTokens: 16384,
        responseFormat: null,
    },
};

// ── Task defaults ───────────────────────────────────────────────────────────
// Lifts the per-task temperature/maxTokens picks scattered across callers
// into a single registry. Extendable without touching any caller.
export const TASK_DEFAULTS = {
    // v5.12.6 (2026-04-22): prompt-audit hygiene pass. Added missing
    // `responseFormat: { type: 'json_object' }` to every task that the
    // audits flagged as JSON-strict via prompt-level discipline only, and
    // lowered `cost-recompute` to near-deterministic temperature (it's
    // structured arithmetic, not creative).

    // Heavy reasoning. Lower temperature, bigger ceiling
    'risk-analysis': { temperature: 0.2, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },
    'quote-generation': { temperature: 0.25, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },
    // Slice C (v5.46.45): advisory quote-vs-RFQ coherence check. JSON verdict;
    // the verifier module sizes max_tokens via getMaxOutputTokens at runtime.
    'rfq-fit-verify': { temperature: 0.1, responseFormat: { type: 'json_object' }, reasoning_effort: 'xhigh' },
    'report-synthesis': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'project-strategy': { temperature: 0.3, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },
    'project-strategy-fallback': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'project-strategy-recovery': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'project-strategy-auto': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'wms-generation': { temperature: 0.25, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },
    'work-method-statement': { temperature: 0.25, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },

    // Classifiers & extractors. Terse, deterministic
    'rfq-mode-classify': { temperature: 0.0, responseFormat: { type: 'json_object' } },
    'doc-classify': { temperature: 0.0, responseFormat: { type: 'json_object' } },
    // Stage 5 Paste quote text: one role per pasted line, JSON only.
    'quote-paste-structure': { temperature: 0.0, responseFormat: { type: 'json_object' } },
    // Canonical batch-classify output is an object containing classifications;
    // the runtime still parses legacy bare arrays from old prompt overrides.
    'batch-classify': { temperature: 0.0, responseFormat: { type: 'json_object' } },
    'contact-extraction': { temperature: 0.0, maxTokens: 4096, responseFormat: { type: 'json_object' } },
    'classification-verifier': { temperature: 0.0, maxTokens: 2048, responseFormat: { type: 'json_object' } },
    'assessor-extraction': { temperature: 0.0, maxTokens: 512, responseFormat: { type: 'json_object' } },
    'notes-scoring': { temperature: 0.2, maxTokens: 2048, responseFormat: { type: 'json_object' } },
    'notes-score': { temperature: 0.2, maxTokens: 2048, responseFormat: { type: 'json_object' } },
    'project-summary': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'evidence-canonicalisation': { temperature: 0.1, responseFormat: { type: 'json_object' } },
    'rationale-precompute': { temperature: 0.3, responseFormat: { type: 'json_object' } },

    // Cost arithmetic. Structured JSON math, near-deterministic.
    'cost-recompute': { temperature: 0.05, responseFormat: { type: 'json_object' } },

    // Deep Thinker (Tier 5). Elevated ceiling + JSON hint (Gemini 3.1 pro
    // preview occasionally drops a trailing brace on 64k output without it).
    //
    // `reasoning_effort: 'high'` is a no-op for non-reasoning models (Gemini
    // pass-a / pass-c): ai-providers.js only forwards it when
    // `isReasoningModel(model) === true`. On Opus 4.7 (pass-b + reconciliation)
    // it routes via OpenRouter's top-level `reasoning` passthrough which maps
    // to Anthropic's `thinking: { type: 'enabled', budget_tokens: N }`,
    // unlocking the reasoning budget the model was shipped with.
    // Kill switch: localStorage.qm_disable_reasoning_effort = 'true'.
    'diagnosis-pass-a': { temperature: 0.3, responseFormat: { type: 'json_object' }, reasoning_effort: 'high' },
    'diagnosis-pass-b': { temperature: 0.3, responseFormat: { type: 'json_object' }, reasoning_effort: 'high' },
    'diagnosis-pass-c': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'diagnosis-reconciliation': { temperature: 0.2, responseFormat: { type: 'json_object' }, reasoning_effort: 'high' },

    // QM Assistant. One assistant, three routes (see model-selector.js):
    //   - `ai-assistant-light`→ Light mode       (DeepSeek V4.1 Flash)
    //   - `ai-assistant`      → normal turn      (DeepSeek V4.1 Flash)
    //   - `ai-assistant-deep` → "Think Harder"   (Opus 5.5)
    // reasoning_effort is forwarded ONLY when `isReasoningModel(model) === true`
    // and the (task, model) pair is not in MEASURED_DEFAULT_EFFORT_ROUTES. The
    // measured Assistant route and its fallbacks run at their provider default
    // effort, which is how they were measured on 2026-09-29; `max` still
    // reaches any other reasoning model an operator routes here (Opus on
    // Think Harder, terra, a manual pick). Unsupported models receive no
    // reasoning parameter because resolveReasoningEffort checks capability.
    // Kill switch: localStorage.qm_disable_reasoning_effort = 'true'.
    // No task maxTokens values live here: assistant-agent.js derives every
    // request from the selected model's verified output ceiling. Keeping a
    // static fallback here is how hidden 16k caps survived model upgrades.
    'ai-assistant': { temperature: 0.2, reasoning_effort: 'max' },
    'ai-assistant-deep': { temperature: 0.2, reasoning_effort: 'max' },
    // Light remains explicit and non-reasoning. If an operator repoints it to a
    // reasoning model we still avoid silently turning a quick lookup into a
    // highest-effort call.
    'ai-assistant-light': { temperature: 0.2 },

    // Vision/media: defaults OK. photo-label gets JSON-mode.
    'photo-description': { temperature: 0.2, maxTokens: 2048 },
    'photo-forensic': { temperature: 0.2, responseFormat: { type: 'json_object' }, reasoning_effort: 'high' },
    'photo-forensic-fallback': { temperature: 0.2, responseFormat: { type: 'json_object' }, reasoning_effort: 'high' },
    // The service supplies the complete strict JSON Schema. Keep the generic
    // registry hint valid on its own for model-inspection/UI consumers.
    'photo-auto-assign': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'photo-auto-assign-fallback': { temperature: 0.3, responseFormat: { type: 'json_object' } },
    'plan-analysis': { temperature: 0.2, responseFormat: { type: 'json_object' } },
    'photo-label': { temperature: 0.3, maxTokens: 2048, responseFormat: { type: 'json_object' } },
    'image-ocr': { temperature: 0.0 },
    'image-text-triage': { temperature: 0.0, responseFormat: { type: 'json_object' } },
    'pdf-ocr': { temperature: 0.0 },

    // v5.12.6: new tasks (unrouted prior to this release)
    'scope-morph': { temperature: 0.3, responseFormat: { type: 'json_object' }, reasoning_effort: 'max' },
    'pdf-comparison': { temperature: 0.1, responseFormat: { type: 'json_object' } },
    'gold-standard-extraction': { temperature: 0.2, responseFormat: { type: 'json_object' } },
};

// ── Family × Task overrides ─────────────────────────────────────────────────
// Narrow surgical overrides. Only populated where a specific family needs to
// deviate from the task default: keeps the matrix sparse and auditable.
export const FAMILY_TASK_OVERRIDES = {
    anthropic: {
        // Anthropic's JSON-mode discipline is strong; keep response_format
        // on by default when the task asks for structured output.
    },
    google: {
        // Gemini tolerates response_format but is slightly less strict about
        // honouring it on Flash. No deviations needed in Phase A.
    },
    openai: {
        // Reasoning models (o-series) ignore temperature. Sanitisation below
        // strips it when we detect one.
    },
    xai: {
        // Grok family has a smaller output ceiling; clamp when needed.
    },
    moonshot: {
        // Kimi K2.6 has fixed sampling contracts. Sanitisation below strips
        // task temperatures/top_p so provider defaults carry the call.
    },
    qwen: {
        // Qwen is useful as a cheap image/video challenger, but it is not an
        // audio route through OpenRouter metadata at time of writing.
    },
    deepseek: {},
    mistral: {},
    meta: {},
    cohere: {},
    unknown: {},
};

export const MODEL_RUNTIME_PROFILES = {
    anthropic: {
        promptStyle: 'xml',
        notes: 'Strong for long-context reasoning and structured synthesis; no audio/video route via OpenRouter metadata.',
    },
    google: {
        promptStyle: 'schema-plus-media',
        notes: 'Gemini handles audio/video/image/PDF routes; Gemini 3 complex reasoning should keep default temperature.',
    },
    moonshot: {
        promptStyle: 'strict-schema',
        notes: 'Kimi K2.6 is a budget reasoning challenger; fixed sampling values must not be overridden.',
    },
    qwen: {
        promptStyle: 'strict-schema',
        notes: 'Cheap image/video challenger; not audio-capable through current OpenRouter metadata.',
    },
    openai: {
        promptStyle: 'schema',
        notes: 'Reasoning models ignore or reject temperature; use reasoning controls where supported.',
    },
    xai: {
        promptStyle: 'schema',
        notes: 'Clamp large output budgets.',
    },
    unknown: {
        promptStyle: 'conservative',
        notes: 'Unknown family: avoid assuming unsupported capabilities.',
    },
};

// Tasks where Gemini 3's documented default-temperature behaviour matters most.
// Keep tiny classifiers/extractors on their existing deterministic settings.
export const GEMINI3_DEFAULT_TEMPERATURE_TASKS = new Set([
    'photo-forensic',
    'photo-analysis',
    'photo-auto-assign',
    'photo-auto-assign-fallback',
    'plan-analysis',
    'diagnosis-pass-a',
    'risk-analysis',
    'project-strategy',
    'project-strategy-fallback',
    'project-strategy-recovery',
    'project-strategy-auto',
    'work-method-statement',
    'wms-generation',
    'quote-generation',
    'ai-assistant',
    'ai-assistant-deep',
    'ai-assistant-light',
]);

export const CRITICAL_PARAMETER_TASKS = new Set([
    'contact-extraction',
    'classification-verifier',
    'cost-recompute',
    'risk-analysis',
    'project-strategy',
    'project-strategy-fallback',
    'project-strategy-recovery',
    'project-strategy-auto',
    'work-method-statement',
    'wms-generation',
    'scope-morph',
    'project-summary',
    'quote-generation',
    'rfq-fit-verify',
    'learning-harvest',
    'harvest-triage',
    'learning-triage',
    'gold-standard-extraction',
    'quote-import-extraction',
    'pdf-comparison',
    'diagnosis-pass-a',
    'diagnosis-pass-b',
    'diagnosis-pass-c',
    'diagnosis-reconciliation',
    'photo-forensic',
    'photo-analysis',
    'photo-auto-assign',
    'photo-auto-assign-fallback',
    'plan-analysis',
    'ai-assistant',
    'ai-assistant-deep',
    'ai-assistant-light',
]);

// ── Family-level sanitisation ───────────────────────────────────────────────
// Applied AFTER the 4-layer merge so we can delete keys the model can't accept.
// Phase A doesn't change behaviour, but the sanitiser runs inside getModelConfig
// so the diff log reflects what Phase B+ would actually send.
function sanitiseForFamily(config, modelId, family, taskName) {
    const out = { ...config };

    // OpenAI reasoning models (o-series) ignore `temperature` and require
    // reasoning-specific params. Strip temperature so OpenRouter doesn't
    // return an error.
    if (family === 'openai' && isReasoningModel(modelId)) {
        delete out.temperature;
    }

    // Gemini 3 guidance changed from the old "lower temp for serious work"
    // habit. For complex reasoning/media tasks, keep Gemini's default 1.0
    // instead of inheriting Claude-style low task temperatures.
    if (family === 'google' && isGemini3Model(modelId) && GEMINI3_DEFAULT_TEMPERATURE_TASKS.has(taskName)) {
        out.temperature = 1.0;
    }

    // Kimi K2.6 and K3.1 Pro have fixed sampling contracts. Sending QuoteMaker's task
    // temperatures/top_p through OpenRouter can reject the call or degrade the
    // intended mode. Let the provider defaults carry Kimi instead.
    if (family === 'moonshot' && isKimiFixedSamplingModel(modelId)) {
        delete out.temperature;
        delete out.top_p;
        delete out.topP;
        delete out.presence_penalty;
        delete out.frequency_penalty;
    }

    // xAI Grok. Keep proposed/future task-config bodies inside the same
    // conservative 32,768 ceiling used by ai-api.js. The old 16,384 clamp
    // contradicted the live model-capability map and could reintroduce a hidden
    // half-ceiling if broad task-config injection is ever enabled.
    if (family === 'xai' && Number.isFinite(out.maxTokens) && out.maxTokens > 32768) {
        out.maxTokens = 32768;
    }

    return out;
}

function _shouldRequireOpenRouterParameters(body, taskName) {
    if (!isRequireOpenRouterParametersEnabled()) return false;
    if (!taskName || !CRITICAL_PARAMETER_TASKS.has(taskName)) return false;
    return !!(
        body?.response_format
        || body?.structured_outputs
        || (Array.isArray(body?.tools) && body.tools.length > 0)
    );
}

/**
 * Strict OpenRouter parameter routing is availability-sensitive, so it is an
 * explicit opt-in in the legacy direct-browser path too. The backend gateway
 * has its own server-side QM_REQUIRE_PARAMETERS switch.
 */
export function isRequireOpenRouterParametersEnabled() {
    try {
        if (import.meta.env?.VITE_QM_REQUIRE_OPENROUTER_PARAMETERS === 'true') return true;
    } catch (_e) { /* import.meta.env unavailable */ }
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('qm_require_openrouter_parameters') === 'true';
        }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
}

function _attachMeta(body, meta) {
    Object.defineProperty(body, '_meta', {
        value: meta,
        enumerable: false,
        writable: false,
    });
    return body;
}

/**
 * Apply active, low-risk runtime profile rules to the real OpenRouter body.
 *
 * This is intentionally narrower than getModelConfig(): it does not inject
 * broad task defaults. It only prevents known-bad family parameters from
 * reaching providers. Strict `require_parameters` routing is applied only
 * when the operator explicitly opts in; response/tool validation remains at
 * the consumer boundary either way.
 */
export function applyModelRuntimeProfile(body, { model, task } = {}) {
    const family = detectFamily(model || body?.model);
    const next = { ...(body || {}) };

    if (family === 'openai' && isReasoningModel(model || next.model)) {
        delete next.temperature;
    }

    if (family === 'google' && isGemini3Model(model || next.model) && GEMINI3_DEFAULT_TEMPERATURE_TASKS.has(task)) {
        next.temperature = 1.0;
    }

    if (family === 'moonshot' && isKimiFixedSamplingModel(model || next.model)) {
        delete next.temperature;
        delete next.top_p;
        delete next.topP;
        delete next.presence_penalty;
        delete next.frequency_penalty;
    }

    if (_shouldRequireOpenRouterParameters(next, task)) {
        next.provider = {
            ...(next.provider || {}),
            require_parameters: true,
        };
    }

    return _attachMeta(next, {
        family,
        task: task || null,
        reasoning: isReasoningModel(model || next.model),
        promptStyle: MODEL_RUNTIME_PROFILES[family]?.promptStyle || MODEL_RUNTIME_PROFILES.unknown.promptStyle,
    });
}

// ── Main resolver ──────────────────────────────────────────────────────────
/**
 * Resolve the effective request-body config for (modelId, task).
 *
 * 4-layer merge (highest wins):
 *   1. FAMILY_DEFAULTS[family]
 *   2. TASK_DEFAULTS[task]
 *   3. FAMILY_TASK_OVERRIDES[family][task]
 *   4. userOverrides argument
 * Then: family-specific sanitisation strips unsupported fields.
 *
 * @param {string} modelId: OpenRouter namespaced model id (e.g. 'anthropic/claude-sonnet-4.6')
 * @param {string} [taskName]. Task identifier from TASK_MODEL_RECOMMENDATIONS. Optional.
 * @param {Object} [userOverrides]. Caller-provided temperature / maxTokens / responseFormat.
 *                                   Undefined fields are ignored (they do NOT overwrite lower layers).
 * @returns {Object} merged + sanitised config: { temperature, maxTokens, responseFormat, _meta }
 */
export function getModelConfig(modelId, taskName, userOverrides = {}) {
    const family = detectFamily(modelId);
    const familyDefaults = FAMILY_DEFAULTS[family] || FAMILY_DEFAULTS.unknown;
    const taskDefaults = (taskName && TASK_DEFAULTS[taskName]) || {};
    const familyTaskOverride = (FAMILY_TASK_OVERRIDES[family] && FAMILY_TASK_OVERRIDES[family][taskName]) || {};

    // 4-layer merge. Only overwrite when the higher layer defines the key
    // (so an undefined userOverride doesn't clobber a meaningful default).
    const merged = { ...familyDefaults };
    for (const [k, v] of Object.entries(taskDefaults)) {
        if (v !== undefined) merged[k] = v;
    }
    for (const [k, v] of Object.entries(familyTaskOverride)) {
        if (v !== undefined) merged[k] = v;
    }
    if (userOverrides && typeof userOverrides === 'object') {
        for (const [k, v] of Object.entries(userOverrides)) {
            if (v !== undefined) merged[k] = v;
        }
    }

    // Family-level sanitisation (strip unsupported fields, clamp ceilings).
    const sanitised = sanitiseForFamily(merged, modelId, family, taskName);

    // Re-shape into the wire body shape (match OpenRouter OpenAI-compatible
    // keys so the diff log compares apples to apples with the legacy body).
    const body = {};
    if (sanitised.temperature !== undefined) body.temperature = sanitised.temperature;
    if (Number.isFinite(sanitised.maxTokens)) body.max_tokens = sanitised.maxTokens;
    if (sanitised.responseFormat) body.response_format = sanitised.responseFormat;
    if (_shouldRequireOpenRouterParameters(body, taskName)) {
        body.provider = { ...(body.provider || {}), require_parameters: true };
    }

    // Attach a non-enumerable _meta for debugging without polluting diffs.
    return _attachMeta(body, {
        family,
        task: taskName || null,
        reasoning: isReasoningModel(modelId),
        promptStyle: MODEL_RUNTIME_PROFILES[family]?.promptStyle || MODEL_RUNTIME_PROFILES.unknown.promptStyle,
    });
}

export default {
    detectFamily,
    isGemini3Model,
    isKimiK26Model,
    isKimiFixedSamplingModel,
    isReasoningModel,
    resolveReasoningEffort,
    MEASURED_DEFAULT_EFFORT_ROUTES,
    reasonsByDefault,
    resolveReasoningDirective,
    SUPPRESS_DEFAULT_REASONING_TASKS,
    FAMILY_DEFAULTS,
    TASK_DEFAULTS,
    FAMILY_TASK_OVERRIDES,
    MODEL_RUNTIME_PROFILES,
    isRequireOpenRouterParametersEnabled,
    applyModelRuntimeProfile,
    getModelConfig,
};
